import { useEffect, useMemo, useRef, useState } from "react";
import { collection, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { onSnapshotResilient } from "../utils/resilientSnapshot";
import type { Interaction } from "../types";
import { TRIP_BATCH_SIZE } from "./useEntityHistory";

export interface UnlinkedInteraction extends Interaction {
  id: string;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Every activity across the given trips that has no place linked to it — the
 * backlog the "Link activities" page works through.
 *
 * Queried by trip rather than by `entityId`, because the interaction rules
 * resolve access through the trip document: a collection-wide
 * `where("entityId", "==", null)` would sweep up other people's trips and be
 * denied outright. Narrowing it to `tripId in [...] AND entityId == null`
 * would be allowed, but needs a composite index — and indexes only deploy from
 * `main`, so that query could never be exercised on a PR before it shipped.
 * The equality-on-`tripId` form runs on the automatic single-field index, and
 * dropping the linked activities on the client also catches any document
 * written without the field at all.
 *
 * These are live listeners, so an activity vanishes from the list the moment
 * it's linked — including when someone else does the linking.
 */
export function useUnlinkedInteractions(tripIds: string[]) {
  const [batches, setBatches] = useState<Record<number, UnlinkedInteraction[]>>({});
  const [loading, setLoading] = useState(true);
  // Batches the server refused. Their activities are missing from the list,
  // so the page says so rather than showing a short backlog as complete.
  const [failedBatches, setFailedBatches] = useState(0);
  // Sorting keeps the key stable when a caller rebuilds the array each render.
  const idsKey = [...new Set(tripIds)].sort().join(",");
  const settled = useRef(new Set<number>());

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    setBatches({});
    setFailedBatches(0);
    settled.current = new Set();
    if (ids.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Not Firestore's 30-value `in` cap: the read rule does a get() of each
    // trip, and a batch of 30 runs out of get() budget and is refused whole.
    const groups = chunk(ids, TRIP_BATCH_SIZE);
    const unsubs = groups.map((group, index) =>
      onSnapshotResilient(
        query(collection(db, "interactions"), where("tripId", "in", group)),
        (snapshot) => {
          const unlinked = snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }) as UnlinkedInteraction)
            .filter((i) => !i.entityId);
          setBatches((prev) => ({ ...prev, [index]: unlinked }));
          settled.current.add(index);
          if (settled.current.size === groups.length) setLoading(false);
        },
        {
          label: `unlinked activities (trips ${index + 1}/${groups.length})`,
          onError: () => {
            setFailedBatches((n) => n + 1);
            settled.current.add(index);
            if (settled.current.size === groups.length) setLoading(false);
          },
        },
      ),
    );

    return () => unsubs.forEach((u) => u());
  }, [idsKey]);

  const interactions = useMemo(
    () => Object.values(batches).flat(),
    [batches],
  );

  return { interactions, loading, incomplete: failedBatches > 0 };
}
